import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useDispatch, useSelector } from 'react-redux';
import { DividerSection } from '../../../../stories/divider-section';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { useApiResponseError } from '../../../global/hooks/useApiError';
import { ApiStatuses } from '../../../global/models/api-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { FormTypes } from '../../account-info/models/form-models';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { ClergyBioChurchRelationship, DataState, RelationshipPerson } from '../model/clergy-bio-app-models';
import EcdMessage from '../summary/ecd-message';
import { ClergyBioRelationshipFormModel } from './model/form-models';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ConstructPayload, FormatRelationshipPerson, GetClergyBioRelationShipValidationSchema, findInnerErrors, getNewRelationshipPerson } from './clergy-bio-relationship-helpers';
import ClergyBioRelationshipPersonFields from './clergy-bio-relationship-person-fields';
import { setClergyBioRelationship, setLoadingState } from '../clergy-bio-slice';
import { getClergyBioRelationships, patchClergyBioRelationships } from '../../../global/ducks/clergy-bio-api';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { apiErrorClear } from '../../../global/ducks/base-api';



function ClergyBioRelationshipUpdate() {
	const buttonClasses = buttonStyles();
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.relationship);
	const relationships = useSelector<TcsmStates, ClergyBioChurchRelationship>(state => state?.clergyBioState?.relationship);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const [errorMaps, setErrorMaps] = useState<any>({});

	const defaultValues = {
		parent_relations_attributes: relationships?.parents?.details,
		child_relations_attributes: relationships?.children?.details,
		currentSpouse: relationships?.currentSpouse?.details,
	};

	const [initialValues, setInitialValues] = useState<ClergyBioRelationshipFormModel>(
		{
			parent_relations_attributes: [getNewRelationshipPerson(), getNewRelationshipPerson()],
			child_relations_attributes: [],
			currentSpouse: [],
			deceasedSpouse: [],
			previousSpouse: [],
		},
	);

	const apiResponseErrors = useApiResponseError();	

	const apiErrors = {
		...apiResponseErrors,
		...errorMaps,		
	};	

	const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Relationship, guid);
			setUpdateStatus(ApiStatuses.Success);
			setErrorMaps(null);
			dispatch(setClergyBioRelationship(res?.data));
		}

		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);

			
			setErrorMaps(findInnerErrors(res?.data));
		}
	};

	const updateRelationship = (values: ClergyBioRelationshipFormModel) => {		
		setErrorMaps(null);
		dispatch(apiErrorClear());
		dispatch(patchClergyBioRelationships(guid, ConstructPayload(values, relationships), onSuccess));
	};
	const validationSchema = GetClergyBioRelationShipValidationSchema(settings, labelSettings);

	const addNewPerson = () => {
		const children = initialValues?.child_relations_attributes;
		children.push(getNewRelationshipPerson(true));
		setInitialValues(
			{ ...initialValues, child_relations_attributes: children },
		);
	};
	useEffect(() => {

		const parents = initialValues?.parent_relations_attributes;

		for (let i = 0; i < relationships?.parents?.details?.length; i += 1) {
			parents[i] = relationships?.parents?.details[i];
		}

		setInitialValues(
			{
				parent_relations_attributes: parents?.map(r=> FormatRelationshipPerson(r)),
				child_relations_attributes: relationships?.children?.details?.map(r=> FormatRelationshipPerson(r)),
				currentSpouse: relationships?.currentSpouse?.details?.map(r=> FormatRelationshipPerson(r)),
				previousSpouse: relationships?.previousSpouse?.details?.map(r=> FormatRelationshipPerson(r)),
				deceasedSpouse: relationships?.deceasedSpouse?.details?.map(r=> FormatRelationshipPerson(r)),
			},
		);

	}, [relationships]);

	useEffect(() => {

		return function cleanUp() {
			dispatch(setLoadingState(DataState.LOADING, 'relationship'));
			dispatch(getClergyBioRelationships(
				guid,
				(data) => dispatch(setClergyBioRelationship(data?.data)),
				() => dispatch(setLoadingState(DataState.ERROR, 'relationship'))),
			);
		};
	}, []);

	function RenderSpouseSection(formProps: any, sectionTitle: string, sectionDescription: string, personItems: RelationshipPerson[], arrayField: string) {

		if (personItems?.length > 0) {
			return (
				<DividerSection sectionText={sectionTitle} sectionDescription={sectionDescription} >
					{
						personItems?.map((a, idx) => {
							return <ClergyBioRelationshipPersonFields
								key={`${a?.firstName || arrayField}_${idx}`}
								arrayField={arrayField}
								id={idx?.toString()}
								settings={settings}
								formikProps={formProps}
								disabled
								showLastNameAtBirth
								showDom
								showClergy
								hideDivider
							/>;
						})
					}
				</DividerSection>);
		}

		return null;
	}

	function RenderContent() {
		return (
			<>
			{
				updateStatus === ApiStatuses.Failure &&
				<ContentSpacer>
					<Banner
						banner={{
							type: 'error',
							label: <p><strong>{errorMessage}</strong></p>,
						}}
					/>
				</ContentSpacer>
			}			
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateRelationship(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<ClergyBioRelationshipFormModel>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								window.location.reload();
							}}
							initialValue={defaultValues}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_RELATIONSHIP_UPDATE] : []}
						>
							<PageHeader headerText={settings?.relationshipTitle} description={settings?.relationshipAboutSectionText} />
							<ContentSpacer>
								<EcdMessage />
							</ContentSpacer>							
							<DividerSection sectionText={settings?.parentLabel} sectionDescription={settings?.parentsExplanationSection} >
								{
									initialValues?.parent_relations_attributes?.map((a, idx) => {
										return <ClergyBioRelationshipPersonFields
											key={`${a?.firstName || 'parent_relations_attributes'}_${idx}`}
											arrayField='parent_relations_attributes'
											id={idx?.toString()}
											settings={settings}
											formikProps={formProps}
											showLastNameAtBirth
											showClergy
										/>;
									})
								}
							</DividerSection>
							<DividerSection sectionText={settings?.currentSpouseLabel || relationships?.currentSpouse.title} sectionDescription={settings?.currentSpouseExplanationSection} >
								{
									(!initialValues?.currentSpouse || initialValues?.currentSpouse?.length === 0) &&
									<ContentSpacer>
										<WysiwygWithReactLink content={settings?.noCurrentSpouseDescription} />
									</ContentSpacer>
								}
								{
									initialValues?.currentSpouse?.map((a, idx) => {
										return <ClergyBioRelationshipPersonFields
											key={`${a?.firstName || 'currentSpouse'}_${idx}`}
											arrayField='currentSpouse'
											id={idx?.toString()}
											settings={settings}
											formikProps={formProps}
											disabled
											showLastNameAtBirth
											showDom
											showClergy
										/>;
									})
								}
							</DividerSection>

							{RenderSpouseSection(formProps, settings?.previousSpouseLabel || relationships?.previousSpouse.title, settings?.previousSpouseExplanationSection, initialValues?.previousSpouse, 'previousSpouse')}
							{RenderSpouseSection(formProps, settings?.deceasedSpouseLabel || relationships?.deceasedSpouse.title, settings?.deceasedSpouseExplanationSection, initialValues?.deceasedSpouse, 'deceasedSpouse')}

							<DividerSection removeDivider sectionText={settings?.childrenTitle} sectionDescription={settings?.childrenExplanationSection} >
								{
									initialValues?.child_relations_attributes?.map((a, idx) => {
										return <ClergyBioRelationshipPersonFields
											key={`${a?.firstName || 'child_relations_attributes'}_${idx}`}
											arrayField='child_relations_attributes'
											id={idx?.toString()}
											settings={settings}
											formikProps={formProps}
											showStepChild
											setList={setInitialValues}
											removable={a?.removable || a?.errors}
											errors={errorMaps?.child_relations_attributes}
											showID
										/>;
									})
								}

								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewPerson()} >{settings?.addChildButtonText}</button>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
			</>
		);
	}

	return (
		<ClergyBioComponentLoader
			state={loadingState}
			content={RenderContent()}
		/>
	);
}


export default ClergyBioRelationshipUpdate;
